import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MessageCircle, Zap } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trackEvent, pageContext } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAKES = ["Audi", "BMW", "Volkswagen", "Ford", "Mercedes-Benz", "Vauxhall", "Skoda", "SEAT", "Nissan", "Honda", "Toyota", "Porsche", "Mini", "Peugeot", "Renault", "Volvo", "Land Rover", "Jaguar", "Fiat", "Mazda", "Other"];
const FUELS = [
  { v: "petrol", label: "Petrol" },
  { v: "diesel", label: "Diesel" },
  { v: "hybrid", label: "Hybrid" },
];
const STAGES = [
  { v: "stage-1", label: "Stage 1" },
  { v: "stage-2", label: "Stage 2" },
  { v: "stage-3", label: "Stage 3 / Custom" },
  { v: "unsure", label: "Not sure yet" },
];

const initial = {
  name: "", email: "", phone: "",
  vehicle_make: "", vehicle_model: "", vehicle_year: "",
  fuel: "", engine_size: "", modifications: "",
  stage_interest: "", notes: "",
};

const inputCls =
  "w-full bg-transparent border-0 border-b border-white/20 rounded-none py-2 h-11 text-base text-white placeholder:text-white/30 focus:outline-none focus:border-volt font-body";

export default function GainsEnquiry() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = ["name", "email", "phone", "vehicle_make", "vehicle_model", "vehicle_year", "fuel"];
    for (const k of required) {
      if (!form[k]) {
        toast.error(`Please fill in "${k.replace("_", " ")}".`);
        return;
      }
    }
    setLoading(true);
    try {
      await axios.post(`${API}/gains-enquiry`, {
        ...form,
        vehicle_year: parseInt(form.vehicle_year, 10),
      });
      toast.success("Enquiry sent — we'll come back with accurate figures within one working day.");
      trackEvent("lead_form_submit", { ...pageContext(), form_name: "gains_enquiry", fuel: form.fuel, vehicle_make: form.vehicle_make, stage_interest: form.stage_interest });
      setSubmitted(true);
    } catch {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="gains"
      className="relative bg-carbon py-24 md:py-32 px-6 md:px-10 border-t border-white/10"
      data-testid="gains-section"
    >
      <div className="max-w-[1600px] mx-auto grid grid-cols-12 gap-6">
        {/* Left */}
        <div className="col-span-12 lg:col-span-5">
          <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-volt mb-6">
            ◉ Real Gains · Real Figures
          </p>
          <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tighter mb-8">
            Want to know <br />
            <span className="text-volt">exactly</span> what your car will make?
          </h2>
          <p className="font-body text-white/60 text-base md:text-lg leading-relaxed max-w-md mb-8">
            No auto-calculator guesswork — we've written thousands of maps and
            know the numbers for your exact engine variant. Send us your car's
            details and we'll come back with real, evidence-based Stage 1, 2
            and 3 figures within one working day.
          </p>

          <div className="border-t border-white/10 pt-6 flex flex-wrap gap-3">
            <a
              href="https://wa.me/447523386312?text=Hi%20Mids%20Tuning%2C%20can%20you%20quote%20gains%20for%20my%20car..."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] text-black px-5 py-3 font-mono text-xs uppercase tracking-widest font-semibold hover:bg-[#128C7E] hover:text-white transition-colors"
              data-testid="gains-whatsapp"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp us instead
            </a>
            <a
              href="tel:07523386312"
              className="inline-flex items-center gap-2 border border-volt/40 text-volt px-5 py-3 font-mono text-xs uppercase tracking-widest hover:bg-volt hover:text-black transition-colors"
            >
              Call · 07523 386312
            </a>
          </div>
        </div>

        {/* Right — form */}
        <div className="col-span-12 lg:col-span-7 lg:pl-16">
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-surface-1 border border-volt p-10 md:p-14"
              data-testid="gains-success"
            >
              <Zap className="w-8 h-8 text-volt mb-4" strokeWidth={1.5} />
              <div className="font-mono text-[11px] uppercase tracking-widest text-volt mb-4">◉ Enquiry received</div>
              <h3 className="font-display uppercase text-3xl md:text-4xl leading-tight mb-4">
                We're on it.
              </h3>
              <p className="text-white/60 leading-relaxed mb-8">
                We'll come back within one working day with accurate Stage 1,
                Stage 2 and Stage 3 figures for your exact vehicle — plus a
                fixed booking quote if you're ready to go.
              </p>
              <button
                onClick={() => { setSubmitted(false); setForm(initial); }}
                className="font-mono text-xs uppercase tracking-widest border border-white/20 px-6 py-3 hover:border-volt hover:text-volt transition-colors"
                data-testid="gains-new"
              >
                Send another →
              </button>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="gains-form">
              <div className="grid grid-cols-2 gap-6">
                <Field label="Your name">
                  <input value={form.name} onChange={(e) => setField("name", e.target.value)} autoComplete="name" className={inputCls} data-testid="gains-name" />
                </Field>
                <Field label="Phone">
                  <input type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={(e) => setField("phone", e.target.value)} className={inputCls} data-testid="gains-phone" />
                </Field>
                <Field label="Email" full>
                  <input type="email" autoComplete="email" value={form.email} onChange={(e) => setField("email", e.target.value)} className={inputCls} data-testid="gains-email" />
                </Field>

                <Field label="Make">
                  <Select value={form.vehicle_make} onValueChange={(v) => setField("vehicle_make", v)}>
                    <SelectTrigger className="bg-transparent border-0 border-b border-white/20 rounded-none focus:border-volt focus:ring-0 h-11 text-white" data-testid="gains-make">
                      <SelectValue placeholder="Select make" />
                    </SelectTrigger>
                    <SelectContent className="bg-surface-1 border-white/10 text-white rounded-none max-h-64">
                      {MAKES.map((m) => (
                        <SelectItem key={m} value={m} className="focus:bg-volt focus:text-black">{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Model">
                  <input value={form.vehicle_model} onChange={(e) => setField("vehicle_model", e.target.value)} placeholder="e.g. Golf R" className={inputCls} data-testid="gains-model" />
                </Field>
                <Field label="Year">
                  <input
                    value={form.vehicle_year}
                    onChange={(e) => setField("vehicle_year", e.target.value.replace(/\D/g, "").slice(0, 4))}
                    placeholder="e.g. 2019"
                    inputMode="numeric"
                    className={inputCls}
                    data-testid="gains-year"
                  />
                </Field>
                <Field label="Fuel">
                  <Select value={form.fuel} onValueChange={(v) => setField("fuel", v)}>
                    <SelectTrigger className="bg-transparent border-0 border-b border-white/20 rounded-none focus:border-volt focus:ring-0 h-11 text-white" data-testid="gains-fuel">
                      <SelectValue placeholder="Petrol / Diesel" />
                    </SelectTrigger>
                    <SelectContent className="bg-surface-1 border-white/10 text-white rounded-none">
                      {FUELS.map((f) => (
                        <SelectItem key={f.v} value={f.v} className="focus:bg-volt focus:text-black">{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="Engine size (optional)">
                  <input value={form.engine_size} onChange={(e) => setField("engine_size", e.target.value)} placeholder="e.g. 2.0 TSI" className={inputCls} data-testid="gains-engine-size" />
                </Field>
                <Field label="Interested in">
                  <Select value={form.stage_interest} onValueChange={(v) => setField("stage_interest", v)}>
                    <SelectTrigger className="bg-transparent border-0 border-b border-white/20 rounded-none focus:border-volt focus:ring-0 h-11 text-white" data-testid="gains-stage">
                      <SelectValue placeholder="Which stage?" />
                    </SelectTrigger>
                    <SelectContent className="bg-surface-1 border-white/10 text-white rounded-none">
                      {STAGES.map((s) => (
                        <SelectItem key={s.v} value={s.v} className="focus:bg-volt focus:text-black">{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="Modifications fitted (optional)" full>
                  <input
                    value={form.modifications}
                    onChange={(e) => setField("modifications", e.target.value)}
                    placeholder="e.g. decat downpipe, high-flow filter"
                    className={inputCls}
                    data-testid="gains-mods"
                  />
                </Field>

                <Field label="Anything else we should know?" full>
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setField("notes", e.target.value)}
                    placeholder="Goals, current mileage, faults, timing…"
                    className={`${inputCls} resize-none`}
                    data-testid="gains-notes"
                  />
                </Field>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-3 bg-volt text-black px-6 py-4 font-mono text-xs uppercase tracking-widest font-semibold hover:bg-white transition-colors duration-300 disabled:opacity-50"
                data-testid="gains-submit"
              >
                {loading ? "Sending…" : "Get my gains estimate →"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : "col-span-2 md:col-span-1"}>
      <label className="font-mono text-[10px] uppercase tracking-widest text-white/50 block mb-2">{label}</label>
      {children}
    </div>
  );
}
